import { Icon } from '@/features/common/Icon';
import { END_LABEL } from '@/features/common/DateRangeFilter/dateRange';
import type { DateRangeFieldProps } from '@/features/common/DateRangeFilter/types/DateRangeFieldProps';
import { dateRangeFilter } from '@/i18n/dictionaries/dateRangeFilter';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * One end of a DateRangeFilter, in MUD's date-input markup: `.date-picker__field` ›
 * `.input-group.input-date-wrapper` › `.input-wrapper` › `input.input` and the `icon-calendar`
 * toggle — never a browser `type="date"`. A date can be typed; the toggle points the calendar at
 * this end.
 */
export function DateRangeField({
  end,
  baseId,
  inputRef,
  draft,
  invalid,
  active,
  onActivate,
  onDraftChange,
  onCommit,
}: DateRangeFieldProps) {
  const { t } = useTranslation(dateRangeFilter);
  const id = `${baseId}-${end}`;
  const name = t(END_LABEL[end]);
  const errorId = `${id}-error`;
  return (
    <div className="date-picker__field" data-date-picker>
      <div className="input-group input-date-wrapper">
        <label className="date-range-filter__label" htmlFor={id}>
          {name}
        </label>
        <div className="input-wrapper">
          <input
            ref={inputRef}
            id={id}
            type="text"
            className="input js-date-picker-input"
            placeholder="DD/MM/YYYY"
            autoComplete="off"
            inputMode="numeric"
            value={draft}
            aria-invalid={invalid ? true : undefined}
            aria-describedby={invalid ? errorId : undefined}
            onFocus={() => onActivate(end)}
            onChange={(e) => onDraftChange(end, e.target.value)}
            onBlur={() => onCommit(end)}
            onKeyDown={(e) => {
              // Inside a filter <form>: Enter here reads the date, it does not run the search.
              if (e.key === 'Enter') {
                e.preventDefault();
                onCommit(end);
              }
            }}
          />
          <button
            type="button"
            className="input-icon right icon-calendar js-date-picker-toggle"
            aria-label={t('openCalendar', { field: name })}
            aria-controls={`${baseId}-calendar`}
            aria-expanded={active}
            onClick={() => {
              onActivate(end);
              inputRef.current?.focus();
            }}
          >
            <Icon name="calendar" className="date-range-filter__calendar-icon" />
          </button>
        </div>
        {invalid && (
          <p className="field-error" id={errorId}>
            {t('invalidDate')}
          </p>
        )}
      </div>
    </div>
  );
}
